import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import logo from "../assets/logo_basic.png";
import heroImage from "../assets/landing-hero.webp";
import AppFooter from "../components/AppFooter";
import "../styles/landing.css";
import SEO from "../components/SEO";
import { organizationSchema } from "../seo/schemas/organizationSchema";
import { websiteSchema } from "../seo/schemas/websiteSchema";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthProvider";


export default function LandingPage() {

  const {
  session,
  initialized,
} = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [params, setParams] = useSearchParams();
  const err = params.get("err");
  const [showErrPopup, setShowErrPopup] = useState(false);

  const [freeToolsOpen, setFreeToolsOpen] = useState(false);

  // =====================================================
// DISPLAY FIRST NAME ONLY
//
// Priority:
//
// 1. Google/Supabase given_name
// 2. first_name
// 3. First word from full_name
// 4. First word from name
// 5. Email prefix fallback
// =====================================================

// =====================================================
// DISPLAY USER FIRST NAME
//
// Use public.users.name first - same source as Dashboard.
// =====================================================

const [
  displayName,
  setDisplayName,
] = useState("");

const [
  showSnapshotPopup,
  setShowSnapshotPopup,
] = useState(false);

useEffect(
  () => {
    const loadLandingUserName =
      async () => {
        try {
          if (
            !initialized ||
            !session?.user
          ) {
            setDisplayName("");
            return;
          }


          const authUser =
            session.user;


          const authUid =
            authUser.id;


          // =============================================
          // SAME USER SOURCE USED BY DASHBOARD
          // =============================================

          const {
            data: dbUser,
            error: dbUserError,
          } =
            await supabase
              .from("users")
              .select("name")
              .eq(
                "auth_uid",
                authUid
              )
              .maybeSingle();


          if (
            dbUserError
          ) {
            console.warn(
              "[LANDING] User name lookup warning:",
              dbUserError.message
            );
          }


          // =============================================
          // PRIORITY:
          //
          // 1. users.name
          // 2. Google full_name
          // 3. Google name
          // 4. given_name
          // 5. email prefix
          // =============================================

          const fullName =
            dbUser?.name ||
            authUser
              ?.user_metadata
              ?.full_name ||
            authUser
              ?.user_metadata
              ?.name ||
            authUser
              ?.user_metadata
              ?.given_name ||
            authUser?.email
              ?.split("@")?.[0] ||
            "Member";


          // =============================================
          // FIRST NAME ONLY
          // =============================================

          const firstName =
            fullName
              .toString()
              .trim()
              .split(/\s+/)[0] ||
            "Member";


          const formattedName =
            firstName
              .charAt(0)
              .toUpperCase() +
            firstName
              .slice(1)
              .toLowerCase();


          setDisplayName(
            formattedName
          );


          console.log(
            "[LANDING] Display name:",
            formattedName
          );
        } catch (error) {
          console.error(
            "[LANDING] Unable to load user name:",
            error
          );


          setDisplayName(
            "Member"
          );
        }
      };


    loadLandingUserName();
  },
  [
    initialized,
    session,
  ]
);
    const handleLogout =
  async () => {
    try {
      await supabase.auth.signOut();

      navigate(
        "/landing",
        {
          replace: true,
        }
      );
    } catch (error) {
      console.error(
        "[LANDING] Logout failed:",
        error
      );
    }
  };

  useEffect(() => {
    if (err === "not_registered") setShowErrPopup(true);
  }, [err]);

  const closePopup = () => {
    setShowErrPopup(false);
    const next = new URLSearchParams(params);
    next.delete("err");
    setParams(next, { replace: true });
  };

  const goToRegister = (plan) => {
  const normalized =
    plan === "deep_audit"
      ? "deep_audit"
      : "snapshot";

  localStorage.setItem(
    "selectedPlan",
    normalized
  );

  localStorage.setItem(
    "registrationSource",
    "career_audit"
  );

  navigate(
    `/register?plan=${encodeURIComponent(
      normalized
    )}`
  );
};

const goToFreeCalculatorRegister =
  async () => {
    // =====================================================
    // SAVE USER INTENT
    // =====================================================

    localStorage.setItem(
      "selectedPlan",
      "free_calculator"
    );

    localStorage.setItem(
      "registrationSource",
      "financial_calculator"
    );

    localStorage.setItem(
      "freeToolIntent",
      "financial_calculator"
    );

    localStorage.setItem(
      "redirect_after_login",
      "/dashboard"
    );


    try {
      // ===================================================
      // CHECK EXISTING SUPABASE SESSION
      // ===================================================

      const {
        data,
        error,
      } =
        await supabase.auth
          .getSession();


      if (error) {
        console.error(
          "[FINANCIAL TOOL] Session check failed:",
          error
        );
      }


      const session =
        data?.session;


      // ===================================================
      // ALREADY LOGGED IN
      //
      // No registration/login required.
      // ===================================================

      if (
        session?.user
      ) {
        console.log(
          "[FINANCIAL TOOL] Existing authenticated user:",
          session.user.id
        );


        navigate(
          "/dashboard",
          {
            replace: false,
          }
        );


        return;
      }
    } catch (error) {
      console.error(
        "[FINANCIAL TOOL] Unable to check session:",
        error
      );
    }


    // =====================================================
    // NOT LOGGED IN
    //
    // Continue to registration flow.
    // =====================================================

    navigate(
      "/register?plan=free_calculator&source=financial_calculator"
    );
  };

const goToAiRiskCalculatorRegister = () => {
  localStorage.setItem(
    "selectedPlan",
    "free_calculator"
  );

  localStorage.setItem(
    "registrationSource",
    "ai_risk_calculator"
  );

  localStorage.setItem(
    "freeToolIntent",
    "ai_risk_calculator"
  );

  localStorage.setItem(
    "redirect_after_login",
    "/dashboard"
  );

  navigate(
    "/register?plan=free_calculator&source=ai_risk_calculator"
  );
};
  useEffect(() => {
    if (mobileOpen) document.body.classList.add("app-lock");
    else document.body.classList.remove("app-lock");

    return () => document.body.classList.remove("app-lock");
  }, [mobileOpen]);

  useEffect(() => {
    if (location.hash === "#pricing") {
      setTimeout(() => {
        document.getElementById("pricing")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  }, [location]);

  const handleRegisterNew = () => {
    localStorage.removeItem("selectedPlan");
    localStorage.removeItem("registrationSource");
    localStorage.removeItem("freeToolIntent");
    localStorage.removeItem("redirect_after_login");
    localStorage.removeItem("oauth_flow");
    localStorage.removeItem("oauth_pending_uid");
    localStorage.removeItem("oauth_pending_email");
    localStorage.removeItem("oauth_pending_name");

    localStorage.removeItem("dashboard_step");
    localStorage.removeItem("questionnaire_form");
    localStorage.removeItem("questionnaire_step");
    localStorage.removeItem("questionnaire_substep");
    localStorage.removeItem("current_assessment_id");
    localStorage.removeItem("payment_started");
    localStorage.removeItem("payment_assessment_id");
    localStorage.removeItem("consentAccepted");

    navigate({ pathname: "/landing", hash: "#pricing" });
  };

  useEffect(() => {
    const scroller = document.querySelector(".app-body");
    if (!scroller) return;

    const reveal = () => {
      const reveals = document.querySelectorAll(".reveal");
      const viewH = scroller.clientHeight;

      reveals.forEach((el) => {
        const top = el.getBoundingClientRect().top;
        const point = 150;
        if (top < viewH - point) el.classList.add("active");
      });
    };

    scroller.addEventListener("scroll", reveal, { passive: true });
    setTimeout(reveal, 50);

    return () => scroller.removeEventListener("scroll", reveal);
  }, []);

  const scrollToPricing = () => {
    const ctaSection = document.getElementById("pricing");
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleMenu = () => setMobileOpen((prev) => !prev);

  // const landingSchema = {
  //   "@context": "https://schema.org",
  //   "@graph": [
  //     {
  //       "@type": "Organization",
  //       "@id": "https://www.reignitespirit.com/#organization",
  //       name: "Reignite Spirit",
  //       url: "https://www.reignitespirit.com/",
  //       logo: {
  //         "@type": "ImageObject",
  //         url: "https://www.reignitespirit.com/logo-basic.avif"
  //       },
  //       foundingDate: "2025-05",
  //       address: {
  //         "@type": "PostalAddress",
  //         streetAddress: "2902, Sky Tower, Shams Al Reem Island",
  //         addressLocality: "Abu Dhabi",
  //         addressRegion: "Abu Dhabi",
  //         addressCountry: "AE"
  //       },
  //       founder: {
  //         "@type": "Person",
  //         name: "Cyndhia Joseph",
  //         sameAs: [
  //           "https://www.linkedin.com/in/cjp25"
  //         ]
  //       },
  //       sameAs: [
  //         "https://www.linkedin.com/company/reignite-spirit-ltd"
  //       ]
  //     },
  //     {
  //       "@type": "WebSite",
  //       "@id": "https://www.reignitespirit.com/#website",
  //       url: "https://www.reignitespirit.com/",
  //       name: "Reignite Spirit",
  //       publisher: {
  //         "@id": "https://www.reignitespirit.com/#organization"
  //       }
  //     },
  //     {
  //       "@type": "Service",
  //       "@id": "https://www.reignitespirit.com/#service",
  //       name: "AI Career Resilience Audit",
  //       url: "https://www.reignitespirit.com/landing",
  //       description:
  //         "AI-powered career resilience audits and transition planning to help professionals identify future-proof opportunities.",
  //       provider: {
  //         "@id": "https://www.reignitespirit.com/#organization"
  //       },
  //       areaServed: "Worldwide"
  //     }
  //   ]
  // };

  return (
    <div className="app-shell bg-[#11111f] text-slate-300 font-sans antialiased">
  <SEO
  title="Career Transition Intelligence"
  description="ReigniteSpirit provides career transition reports, career intelligence tools and financial runway planning for professionals."
  path="/landing"
  image="/og-image.jpeg"
  keywords="AI career, career audit, career transition, future jobs, AI resilient career, ReigniteSpirit"
  robots="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
  schema={[
    organizationSchema,
    websiteSchema,
  ]}
/>
      <header className="app-header glass-card border-b border-slate-700/50">
        <nav className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                <div
                  className="flex items-center gap-3 cursor-pointer min-w-0"
                  onClick={() => navigate("/landing")}
                >
                  <img
                    src={logo}
                    alt="Reignite Spirit Logo"
                    className="w-10 sm:w-12 h-10 sm:h-12 drop-shadow-[0_0_14px_#eb6fbd] flex-shrink-0"
                    width="48"
                    height="48"
                    loading="eager"
                    decoding="async"
                  />
                <div className="min-w-[190px]">
  <h1 className="text-base sm:text-lg font-semibold tracking-wide text-[#EB6AB5] whitespace-nowrap">
    REIGNITE.SPIRIT
  </h1>
  <p className="text-[10px] sm:text-xs text-[#EB6AB5]/80 tracking-[0.15em] uppercase whitespace-nowrap">
    Rekindle Purpose
  </p>
</div>                </div>

                <Link
                  to="/foundation-truth"
                  className="hidden md:block text-[#fbbf24] font-bold uppercase tracking-widest text-sm border-b-2 border-[#fbbf24] hover:text-white hover:border-white transition-all animate-pulse"
                >
                  Our Founding Truth
                </Link>
              </div>

              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  <a
  href="#data-reality"
  className="hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
>
  AI Impact
</a>

<div className="relative group">
  <div
    className="relative"
    onMouseEnter={() => setFreeToolsOpen(true)}
    onMouseLeave={() => setFreeToolsOpen(false)}
  >
    <button
      type="button"
      onClick={() => setFreeToolsOpen((prev) => !prev)}
      className="hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
    >
      Free Tools
      <span className="text-xs">▾</span>
    </button>

    {freeToolsOpen && (
      <div className="absolute left-0 top-full z-50 min-w-[330px] rounded-xl border border-[#EB6AB5]/25 bg-[#11111f] shadow-2xl shadow-black/40 p-2">
        <button
          type="button"
          onClick={() => {
            setFreeToolsOpen(false);
            goToFreeCalculatorRegister();
          }}
          className="w-full text-left px-4 py-3 rounded-lg text-sm text-slate-200 hover:bg-[#EB6AB5]/15 hover:text-white transition"
        >
          <span className="block font-semibold text-white">
            💰 Financial Runway Calculator
          </span>
          <span className="block mt-1 text-xs text-slate-400 leading-relaxed">
            Estimate how long your savings and financial resources can support a career transition.
          </span>
        </button>

        <div className="my-1 border-t border-white/10" />

        <button
          type="button"
          onClick={() => {
            setFreeToolsOpen(false);
            goToAiRiskCalculatorRegister();
          }}
          className="w-full text-left px-4 py-3 rounded-lg text-sm text-slate-200 hover:bg-[#EB6AB5]/15 hover:text-white transition"
        >
          <span className="block font-semibold text-white">
            🤖 AI Career Risk Calculator
          </span>
          <span className="block mt-1 text-xs text-slate-400 leading-relaxed">
            Discover how AI and automation may transform your current role and identify your strongest human advantages.
          </span>
        </button>
      </div>
    )}
  </div>
</div>

<a
  href="#how-it-works"
  className="hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
>
  Career Audit Process
</a>
<button
  onClick={() => {
    setMobileOpen(false);
    navigate("/blog");
  }}
  className="text-gray-300 hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium"
>
  Blog
</button>
{/* KEEP EVERYTHING ABOVE THIS */}

<button
  onClick={() => navigate("/faq")}
  className="hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
>
  FAQ
</button>

{/* =====================================================
    REPLACE THE OLD LOGIN / REGISTER / GET MY AUDIT
    BUTTONS FROM HERE
===================================================== */}

{initialized && session ? (
  <>
    {/* =========================================
        LOGGED-IN USER
    ========================================= */}

    <div
      className="
        hidden
        lg:flex
        items-center
        px-3
        py-2
        text-sm
        font-semibold
        text-[#EB6AB5]
        whitespace-nowrap
      "
    >
      Hi {displayName} 👋
    </div>

    <button
      type="button"
      onClick={() =>
        navigate("/dashboard")
      }
      className="
        px-4
        py-2
        text-sm
        font-bold
        rounded-lg
        border
        border-[#EB6AB5]/50
        text-white
        hover:bg-[#EB6AB5]/15
        transition-all
      "
    >
      Dashboard
    </button>

    <button
      type="button"
      onClick={handleLogout}
      className="
        px-4
        py-2
        text-sm
        font-bold
        rounded-lg
        border
        border-white/20
        text-slate-300
        hover:bg-white/10
        hover:text-white
        transition-all
      "
    >
      Logout
    </button>
  </>
) : (
  <>
    {/* =========================================
        NOT LOGGED IN
    ========================================= */}

    <button
      type="button"
      onClick={() =>
        navigate("/login")
      }
      className="
        px-4
        py-2
        text-sm
        font-bold
        rounded-lg
        border
        border-white/30
        text-white
        hover:bg-white
        hover:text-[#11111f]
        transition-all
      "
    >
      Login
    </button>

    <button
      type="button"
      onClick={handleRegisterNew}
      className="
        px-4
        py-2
        text-sm
        font-bold
        rounded-lg
        border
        border-white/30
        text-white
        hover:bg-white
        hover:text-[#11111f]
        transition-all
      "
    >
      Register New
    </button>
  </>
)}

{/* =========================================
    GET MY AUDIT
========================================= */}

<button
  type="button"
  onClick={() => {
    if (session) {
      navigate("/questionnaire");
      return;
    }

    scrollToPricing();
  }}
  className="
    bg-[#cb01bd]
    hover:bg-[#e94ce0]
    text-white
    px-4
    py-2
    rounded-lg
    text-sm
    font-bold
    transition-all
    transform
    hover:scale-105
    shadow-[0_0_15px_rgba(203,1,189,0.5)]
  "
>
  {session
    ? "My Career Audit"
    : "Get My Audit"}
</button>

{/* KEEP EVERYTHING BELOW THIS */}                </div>
              </div>

              <div className="-mr-2 flex md:hidden">
                <button
                  onClick={toggleMenu}
                  className="inline-flex items-center justify-center w-12 h-12 rounded-md text-[#EB6AB5] hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#cb01bd]/50 transition-all"
                  aria-label={mobileOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileOpen}
                  aria-controls="mobile-menu"
                  type="button"
                >
                  <span className="relative w-6 h-5 block">
                    <span
                      className={`absolute left-0 top-0 h-[2.5px] w-6 rounded bg-current transition-all duration-300 ${
                        mobileOpen ? "top-2 rotate-45" : ""
                      }`}
                    />
                    <span
                      className={`absolute left-0 top-2 h-[2.5px] w-6 rounded bg-current transition-all duration-300 ${
                        mobileOpen ? "opacity-0" : "opacity-100"
                      }`}
                    />
                    <span
                      className={`absolute left-0 top-4 h-[2.5px] w-6 rounded bg-current transition-all duration-300 ${
                        mobileOpen ? "top-2 -rotate-45" : ""
                      }`}
                    />
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div
  id="mobile-menu"
  className={`${mobileOpen ? "block" : "hidden"} md:hidden bg-[#1a1a2e] border-t border-white/10`}
>
  <div className="px-3 pt-3 pb-4 space-y-2">
    <button
      onClick={() => {
        setMobileOpen(false);
        navigate("/foundation-truth");
      }}
      className="text-[#fbbf24] hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-bold"
    >
      Our Founding Truth
    </button>

    <a
      href="#data-reality"
      onClick={() => setMobileOpen(false)}
      className="text-gray-300 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
    >
      AI Impact
    </a>
<div className="rounded-lg border border-white/10 bg-white/[0.03] p-2">
  <p className="px-3 pt-2 pb-1 text-xs font-bold uppercase tracking-widest text-[#EB6AB5]">
    Free Tools
  </p>

  <button
    type="button"
    onClick={() => {
      setMobileOpen(false);
      goToFreeCalculatorRegister();
    }}
    className="text-gray-300 hover:text-white hover:bg-white/10 block w-full text-left px-3 py-3 rounded-md text-base font-medium transition"
  >
    💰 Financial Runway Calculator
  </button>

  <button
    type="button"
    onClick={() => {
      setMobileOpen(false);
      goToAiRiskCalculatorRegister();
    }}
    className="text-gray-300 hover:text-white hover:bg-white/10 block w-full text-left px-3 py-3 rounded-md text-base font-medium transition"
  >
    🤖 AI Career Risk Calculator
  </button>
</div>
    <a
      href="#how-it-works"
      onClick={() => setMobileOpen(false)}
      className="text-gray-300 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
    >
      Career Audit Process
    </a>

    <button
  onClick={() => {
    setMobileOpen(false);
    navigate("/blog");
  }}
  className="text-gray-300 hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium"
>
  Blog
</button>

    <button
      onClick={() => {
        setMobileOpen(false);
        navigate("/faq");
      }}
      className="text-gray-300 hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium"
    >
      FAQ
    </button>

    {/* =========================================
    MOBILE AUTH / ACCOUNT SECTION
========================================= */}

{initialized && session ? (
  <>
    <div
      className="
        px-3
        py-2
        text-[#EB6AB5]
        font-semibold
        text-base
      "
    >
      Hi {displayName} 👋
    </div>

    <button
      type="button"
      onClick={() => {
        setMobileOpen(false);
        navigate("/dashboard");
      }}
      className="
        w-full
        text-left
        px-3
        py-2
        rounded-md
        text-base
        font-bold
        text-white
        hover:bg-white/10
        transition
      "
    >
      Dashboard
    </button>

    <button
      type="button"
      onClick={async () => {
        setMobileOpen(false);
        await handleLogout();
      }}
      className="
        w-full
        text-left
        px-3
        py-2
        rounded-md
        text-base
        font-bold
        text-slate-300
        hover:bg-white/10
        hover:text-white
        transition
      "
    >
      Logout
    </button>
  </>
) : (
  <>
    <button
      type="button"
      onClick={() => {
        setMobileOpen(false);
        navigate("/login");
      }}
      className="
        w-full
        text-left
        text-white
        hover:bg-white/10
        px-3
        py-2
        rounded-md
        text-base
        font-bold
        transition
      "
    >
      Login
    </button>

    <button
      type="button"
      onClick={() => {
        setMobileOpen(false);
        handleRegisterNew();
      }}
      className="
        w-full
        text-left
        px-3
        py-2
        rounded-md
        text-base
        font-bold
        border
        border-white/30
        text-white
        hover:bg-white
        hover:text-[#11111f]
        transition-all
      "
    >
      Register New
    </button>
  </>
)}

<button
  type="button"
  onClick={() => {
    setMobileOpen(false);

    if (session) {
      navigate("/questionnaire");
      return;
    }

    scrollToPricing();
  }}
  className="
    w-full
    px-3
    py-2
    rounded-md
    text-base
    font-bold
    bg-[#cb01bd]
    hover:bg-[#e94ce0]
    text-white
    transition-all
  "
>
  {session
    ? "My Career Audit"
    : "Get My Audit"}
</button>
  </div>
</div>
        </nav>
      </header>

      <main className="app-body overflow-x-hidden scroll-smooth">
        <section className="hero-bg min-h-[85svh] md:min-h-[100dvh] flex items-center justify-center">
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            width="1400"
            height="900"
            sizes="100vw"
            className="hero-image"
          />

          <div className="hero-overlay"></div>

          <div className="hero-content max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-10 pb-16">
           <h1 className="text-[2.2rem] sm:text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight drop-shadow-2xl">
  Build a Career That AI Can’t Replace Right Where You Live.
</h1>

            <p className="mt-4 max-w-3xl mx-auto text-base sm:text-lg md:text-xl text-slate-300 mb-8 sm:mb-10 leading-relaxed">
  Generic career advice doesn't work for unique local job markets. Reignite Spirit checks your daily job tasks against real-world automation risks and city-specific hiring trends. Instead of guessing, you get a clear, practical roadmap to transition into a high-growth local career that's safe from AI displacement.
</p>

            {err === "not_registered" && (
              <p className="text-red-400 text-center mb-4 font-semibold">
                This Google account is not registered. Please select a plan and register.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={scrollToPricing}
                className="w-full sm:w-auto px-7 sm:px-8 py-4 bg-[#cb01bd] hover:bg-[#e94ce0] text-white text-base sm:text-lg font-bold rounded-xl shadow-lg shadow-[#cb01bd]/40 transition-all transform hover:-translate-y-1 hover:shadow-[#cb01bd]/60 flex items-center justify-center gap-3 group border border-white/10"
              >
                <span>Start My Career Audit</span>
              </button>

              <a
                href="#how-it-works"
                className="text-slate-300 hover:text-white font-medium underline decoration-[#cb01bd] underline-offset-4 transition-colors"
              >
                How does it work?
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-[#11111f] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16 reveal">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                The Old Career Ladder is Gone.
              </h2>
              <p className="text-base sm:text-lg text-slate-400">
                Trying to "out-work" AI is a losing battle. The secret isn't learning more hard skills—
                it's leveraging the human traits that algorithms can't simulate.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              <div className="glass-card p-7 sm:p-8 rounded-2xl hover:bg-[#1a1a2e] transition-all duration-300 border-l-4 border-red-500 reveal delay-100 group">
                <div className="h-12 w-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                  <i className="fa-solid fa-robot text-red-500 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">The AI Threat</h3>
                <p className="text-slate-400 leading-relaxed">
                  Routine cognitive tasks are being automated at record speed. If your job relies on repetition, you are at risk.
                </p>
              </div>

              <div className="glass-card p-7 sm:p-8 rounded-2xl hover:bg-[#1a1a2e] transition-all duration-300 border-l-4 border-yellow-500 reveal delay-200 group">
                <div className="h-12 w-12 bg-yellow-500/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-500/20 transition-colors">
                  <i className="fa-solid fa-triangle-exclamation text-yellow-500 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">The Generic Trap</h3>
                <p className="text-slate-400 leading-relaxed">
                  Generic "Learn to Code" bootcamps are failing. You don't need a generic skill; you need a competitive advantage.
                </p>
              </div>

              <div className="glass-card p-7 sm:p-8 rounded-2xl hover:bg-[#1a1a2e] transition-all duration-300 border-l-4 border-[#cb01bd] reveal delay-300 group">
                <div className="h-12 w-12 bg-[#cb01bd]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#cb01bd]/20 transition-colors">
                  <i className="fa-solid fa-fingerprint text-[#cb01bd] text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">The Human Edge</h3>
                <p className="text-slate-400 leading-relaxed">
                  Your creativity, empathy, and strategic intuition are your lifeboat. We help you turn them into a career engine.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="data-reality" className="py-20 sm:py-24 bg-[#161626] border-y border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16 reveal">
              <span className="text-[#cb01bd] font-bold tracking-wider uppercase text-sm">
                The Industry Reality
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mt-2">
                Global Job Automation Risk
              </h2>
              <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
                Leading Research Confirms: The shift is Structural Immediate & Massive. Passive employees will be left behind. A Career Pivot Strategy is vital.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-[#11111f] p-7 sm:p-8 rounded-2xl border border-slate-700/50 relative overflow-hidden reveal hover:border-[#cb01bd]/50 transition-colors duration-300">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-[#cb01bd]/10 rounded-full blur-2xl"></div>
                <div className="text-4xl sm:text-5xl font-extrabold stat-number mb-2">300M</div>
                <h4 className="text-xl font-bold text-white mb-4">Jobs Exposed</h4>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  Goldman Sachs estimates that generative AI could expose the equivalent of{" "}
                  <strong className="text-white">300 million full-time jobs</strong> to automation across major economies.
                </p>
                <div className="mt-auto pt-4 border-t border-slate-800 text-xs text-slate-500 font-semibold uppercase tracking-wider flex items-center gap-2">
                  <i className="fa-solid fa-file-lines"></i> Goldman Sachs Research
                </div>
              </div>

              <div className="bg-[#11111f] p-7 sm:p-8 rounded-2xl border border-slate-700/50 relative overflow-hidden reveal delay-100 hover:border-[#cb01bd]/50 transition-colors duration-300">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
                <div className="text-4xl sm:text-5xl font-extrabold stat-number mb-2">50%</div>
                <h4 className="text-xl font-bold text-white mb-4">Work Activities</h4>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  McKinsey's "Future of Work" report states that current technologies have the potential to automate activities that absorb{" "}
                  <strong className="text-white">50% of employee time</strong>.
                </p>
                <div className="mt-auto pt-4 border-t border-slate-800 text-xs text-slate-500 font-semibold uppercase tracking-wider flex items-center gap-2">
                  <i className="fa-solid fa-chart-pie"></i> McKinsey Global Institute
                </div>
              </div>

              <div className="bg-[#11111f] p-7 sm:p-8 rounded-2xl border border-slate-700/50 relative overflow-hidden reveal delay-200 hover:border-[#cb01bd]/50 transition-colors duration-300">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl"></div>
                <div className="text-4xl sm:text-5xl font-extrabold stat-number mb-2">44%</div>
                <h4 className="text-xl font-bold text-white mb-4">Skill Disruption</h4>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  The World Economic Forum predicts that{" "}
                  <strong className="text-white">44% of workers' core skills</strong> will be disrupted in the next five years.
                </p>
                <div className="mt-auto pt-4 border-t border-slate-800 text-xs text-slate-500 font-semibold uppercase tracking-wider flex items-center gap-2">
                  <i className="fa-solid fa-globe"></i> WEF Future of Jobs
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="py-16 sm:py-20 bg-[#1a1a2e]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16 reveal">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white">
                Precision Skill Mapping: How Our AI Career Audit Navigates Your Job Transition
              </h2>
              <div className="w-20 h-1 bg-[#cb01bd] mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="relative">
              <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-700 -translate-y-1/2 z-0"></div>

              <div className="grid md:grid-cols-3 gap-8 sm:gap-12 relative z-10">
                <div className="bg-[#11111f] p-7 sm:p-8 rounded-2xl text-center border border-slate-700 shadow-xl reveal hover:-translate-y-2 transition-transform duration-300">
                  <div className="w-16 h-16 bg-[#cb01bd] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-[#cb01bd]/40">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">AI Impact Assessment</h3>
                  <p className="text-slate-400 text-sm">
                    Identify your unique skills and which of your current job activities are at risk.
                  </p>
                </div>

                <div className="bg-[#11111f] p-7 sm:p-8 rounded-2xl text-center border border-slate-700 shadow-xl reveal delay-100 hover:-translate-y-2 transition-transform duration-300">
                  <div className="w-16 h-16 bg-[#cb01bd] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-[#cb01bd]/40">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">The Skill Gap Analysis</h3>
                  <p className="text-slate-400 text-sm">
                    Map your existing job expertise and determine skill gaps to fill for AI resilient emerging jobs.
                  </p>
                </div>

                <div className="bg-[#11111f] p-7 sm:p-8 rounded-2xl text-center border border-slate-700 shadow-xl reveal delay-200 hover:-translate-y-2 transition-transform duration-300">
                  <div className="w-16 h-16 bg-[#cb01bd] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-[#cb01bd]/40">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Your Strategic Career Roadmap</h3>
                  <p className="text-slate-400 text-sm">
                    Receive your PDF Career Report, localised to your target city, with job pathways and a time-bound reskilling plan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
<section
  id="free-tools"
  className="py-16 bg-[#161626] border-y border-[#EB6AB5]/20"
>
  <div className="max-w-6xl mx-auto px-4">
    <div className="text-center mb-10">
      <p className="text-[#EB6AB5] font-bold tracking-[0.2em] uppercase text-sm mb-3">
        Free Tools
      </p>

      <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
        Start Planning Your Career Transition for Free
      </h2>

      <p className="text-slate-300 max-w-3xl mx-auto">
        Use our free tools to understand both your exposure to AI and your financial readiness before making your next career move.
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      {/* Financial Calculator */}
      <div className="rounded-3xl border border-[#EB6AB5]/30 bg-[#11111f]/80 p-8 md:p-10 flex flex-col">
        <div className="text-4xl mb-5">💰</div>

        <p className="text-[#EB6AB5] font-bold tracking-[0.18em] uppercase text-xs mb-3">
          Free Financial Tool
        </p>

        <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
          Financial Runway Calculator
        </h3>

        <p className="text-slate-300 leading-relaxed mb-7 flex-1">
          Estimate how long your savings, income, benefits and financial resources may support your career transition.
        </p>

        <button
          type="button"
          onClick={goToFreeCalculatorRegister}
          className="w-full px-8 py-4 rounded-xl bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold shadow-lg shadow-[#cb01bd]/30 transition"
        >
          Use Financial Calculator →
        </button>
      </div>

      {/* AI Risk Calculator */}
      <div className="rounded-3xl border border-[#EB6AB5]/30 bg-[#11111f]/80 p-8 md:p-10 flex flex-col">
        <div className="text-4xl mb-5">🤖</div>

        <p className="text-[#EB6AB5] font-bold tracking-[0.18em] uppercase text-xs mb-3">
          Free AI Tool
        </p>

        <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
          AI Career Risk Calculator
        </h3>

        <p className="text-slate-300 leading-relaxed mb-7 flex-1">
          Understand how AI may transform your current role, identify the tasks most exposed to automation and discover your strongest human advantages.
        </p>

        <button
          type="button"
          onClick={goToAiRiskCalculatorRegister}
          className="w-full px-8 py-4 rounded-xl bg-gradient-to-r from-[#cb01bd] to-[#EB6AB5] hover:from-[#e94ce0] hover:to-[#ff83d1] text-white font-bold shadow-lg shadow-[#cb01bd]/30 transition"
        >
          Check My AI Career Risk →
        </button>
      </div>
    </div>
  </div>
</section>
        <section id="pricing" className="py-20 sm:py-24 relative overflow-hidden bg-[#11111f] border-t border-slate-800">
  <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e] to-[#11111f]"></div>

  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <div className="text-center mb-12 sm:mb-16">
    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
  Choose Your Clarity Level
</h2>

<p className="text-white mt-4 text-lg sm:text-xl font-semibold">
  Own your Career Roadmap for Free!
</p>

<p className="text-[#fbbf24] font-bold text-base sm:text-lg mt-2">
  For a limited time only
</p>

<p className="text-[#22c55e] font-bold text-base sm:text-lg mt-3">
  Our launch special: Unlock premium insights free!
</p>
    </div>

    <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto items-start">
      {/* Deep Audit Column */}
      <div className="flex flex-col gap-5">
        <div className="flex justify-center">
          <a
            href="/samples/Sample Report Deep Dive - Admin and Accounts.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-w-[230px] px-6 py-3 rounded-full border-2 border-[#fbbf24] text-[#fbbf24] font-semibold text-sm sm:text-base hover:bg-[#fbbf24] hover:text-black transition-all shadow-[0_0_12px_rgba(251,191,36,0.18)]"
          >
            View Sample Deep Audit
          </a>
        </div>

        <div
          onClick={() => setSelectedPlan("deep_audit")}
          className={`glass-card rounded-2xl p-7 sm:p-8 border transition-all flex flex-col relative cursor-pointer transform md:scale-105 ${
            selectedPlan === "deep_audit"
              ? "border-[#fbbf24] ring-2 ring-[#fbbf24]"
              : "border-[#fbbf24] hover:shadow-2xl"
          }`}
        >
          <div className="absolute top-0 right-0 bg-[#fbbf24] text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
            RECOMMENDED
          </div>

          <h3 className="text-2xl font-bold text-white mb-2">The Deep Audit</h3>
<div className="mb-6">
  <div className="flex flex-wrap items-center gap-3">
    {/* OLD PRICE - CROSSED OUT */}

    <span
      className="
        relative
        inline-block
        text-3xl
        sm:text-4xl
        font-extrabold
        text-slate-400
      "
    >
      US$19.99

      <span
        className="
          absolute
          left-[-4%]
          top-1/2
          h-[4px]
          w-[108%]
          -translate-y-1/2
          -rotate-6
          rounded-full
          bg-red-500
          shadow-[0_0_10px_rgba(239,68,68,0.95)]
        "
      />
    </span>

    <span className="text-sm text-slate-500 font-normal">
      one-time
    </span>
  </div>

  {/* TEMPORARY FREE PRICE */}

  <div
    className="
      mt-3
      text-3xl
      sm:text-4xl
      font-extrabold
      text-[#22c55e]
      drop-shadow-[0_0_12px_rgba(34,197,94,0.35)]
    "
  >
    FREE
  </div>

  <p className="mt-2 text-sm font-semibold text-[#fbbf24]">
    Launch Special — Limited Time
  </p>
</div>

          <p className="text-slate-400 text-sm mb-8">
            The complete toolkit to pivot your career safely.
          </p>

          <ul className="space-y-4 mb-6">
            <li className="text-sm text-slate-300">✓ Current Job Impact Assessment Due to AI</li>
            <li className="text-sm text-slate-300">✓ Unique Skills Assessment</li>
            <li className="text-sm text-slate-300">✓ 3 AI Resilient Career Pathways in Your city localised (For Deep Dive only)</li>
            <li className="text-sm text-slate-300">✓ Skill Gap Assessment and Recommended reskilling courses for recommended job roles</li>
            <li className="text-sm text-slate-300">✓ Interest Profiler O*NET Analysis considered (For Deep Dive only)</li>
            <li className="text-sm text-slate-300">✓ Resume Analyzer - ATS Scoring and Improvement (For Deep Dive only)</li>
            <li className="text-sm text-slate-300">✓ Free access to upcoming Tools like Financial Calculator, AI Micro-skilling Journeys and Forum Memberships</li>
            <li className="text-sm text-slate-300">✓ Substantially Discounted Access to upcoming features like Job Matching Pools</li>
            <li className="text-sm text-slate-300">
  <strong>Regional Market Calibration:</strong> Our models analyze your job parameters against real-world city hiring indices and corporate automation trends, ensuring your mapped directions fit local market realities.
</li>

<li className="text-sm text-slate-300">
  <strong>Localized Financial Runway Analytics:</strong> Access dedicated runway calculators built for regional cost-of-living adjustments, local currency buffers, and local employment compliance structures.
</li>

<li className="text-sm text-slate-300">
  <strong>Regional ATS Matching:</strong> Scans resume compliance variables against applicant tracking filters configured specifically for regional corporate frameworks.
</li>
          </ul>

          <div className="flex flex-col gap-3 mt-auto">
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToRegister("deep_audit");
              }}
              className="w-full py-3 bg-[#fbbf24] text-black font-bold rounded-lg hover:bg-yellow-400 transition shadow-lg"
            >
              Start Deep Audit →
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/deep-audit");
              }}
              className="w-full py-3 border border-[#fbbf24] text-[#fbbf24] font-bold rounded-lg hover:bg-[#fbbf24] hover:text-black transition"
            >
              View Details
            </button>
          </div>
        </div>
      </div>

      {/* Snapshot Column */}
      <div className="flex flex-col gap-5">
        <div className="flex justify-center">
          <a
            href="/samples/Sample Report Snap Shot - Creative Group Head.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-w-[230px] px-6 py-3 rounded-full border-2 border-[#cb01bd] text-[#cb01bd] font-semibold text-sm sm:text-base hover:bg-[#cb01bd] hover:text-white transition-all shadow-[0_0_12px_rgba(203,1,189,0.18)]"
          >
            View Sample Snapshot Report
          </a>
        </div>

        <div
          onClick={() => setSelectedPlan("snapshot")}
          className={`glass-card rounded-2xl p-7 sm:p-8 border transition-all flex flex-col relative cursor-pointer ${
            selectedPlan === "snapshot"
              ? "border-[#cb01bd] ring-2 ring-[#cb01bd]"
              : "border-slate-700 hover:border-[#cb01bd]"
          }`}
        >
          <h3 className="text-2xl font-bold text-white mb-2">The Snapshot</h3>

          <div className="mb-6">
  <div className="flex flex-wrap items-center gap-3">
    {/* OLD PRICE - CROSSED OUT */}

    <span
      className="
        relative
        inline-block
        text-3xl
        sm:text-4xl
        font-extrabold
        text-slate-400
      "
    >
      US$9.99

      <span
        className="
          absolute
          left-[-4%]
          top-1/2
          h-[4px]
          w-[108%]
          -translate-y-1/2
          rotate-6
          rounded-full
          bg-red-500
          shadow-[0_0_10px_rgba(239,68,68,0.95)]
        "
      />
    </span>

    <span className="text-sm text-slate-500 font-normal">
      one-time
    </span>
  </div>

  {/* TEMPORARY FREE PRICE */}

  <div
    className="
      mt-3
      text-3xl
      sm:text-4xl
      font-extrabold
      text-[#22c55e]
      drop-shadow-[0_0_12px_rgba(34,197,94,0.35)]
    "
  >
    FREE
  </div>

  <p className="mt-2 text-sm font-semibold text-[#fbbf24]">
    Launch Special — Limited Time
  </p>
</div>

          <p className="text-slate-400 text-sm mb-8">
            Perfect for a quick reality check on your current role.
          </p>

          <ul className="space-y-4 mb-6">
            <li className="text-sm text-slate-300">✓ Job Impact Assessment due to AI</li>
            <li className="text-sm text-slate-300">✓ Unique Skills Assessment</li>
            <li className="text-sm text-slate-300">✓ 1 AI Resilient Career Pathway in Your City</li>
            <li className="text-sm text-slate-300">✓ Skill Gap assessment and recommended reskilling courses for recommended job roles</li>
            <li className="text-sm text-slate-300">✓ Free access to upcoming Tools like Financial Calculator, AI Micro-skilling Journeys and Forum Memberships</li>
          </ul>

          <div className="flex flex-col gap-3 mt-auto">
           <button
  onClick={(e) => {
    e.stopPropagation();

    setShowSnapshotPopup(
      true
    );
  }}
  className="w-full py-3 border border-[#cb01bd] text-[#cb01bd] font-bold rounded-lg hover:bg-[#cb01bd] hover:text-white transition"
>
  Get Snapshot →
</button>


            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/snapshot");
              }}
              className="w-full py-3 border border-slate-600 text-white font-bold rounded-lg hover:bg-white hover:text-[#11111f] transition"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
        <section id="cta-section" className="py-20 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#2a0f35] to-[#11111f] opacity-90"></div>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')",
              opacity: 0.05,
            }}
          ></div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 reveal">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-6">
              "Chance favors the prepared mind!"
              <br />
              <span className="text-lg sm:text-2xl font-normal text-slate-400 mt-2 block">
                - Louis Pasteur
              </span>
            </h2>
            <p className="text-base sm:text-xl text-purple-200 mb-10">
              Your talents are unique. Your future should be too.
              <br />
              Get your personalized resilience roadmap today.
            </p>

            <a
              href="#pricing"
              className="inline-block w-full md:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-white text-[#cb01bd] hover:bg-slate-100 text-lg sm:text-xl font-extrabold rounded-full shadow-2xl transition-transform transform hover:scale-105 animate-pulse-slow ring-4 ring-[#cb01bd]/20"
            >
              Start Planning Your Ascent
            </a>

            <p className="mt-6 text-sm text-purple-300 opacity-70">
              100% Private &amp; Secure Analysis
            </p>
          </div>
        </section>

        <AppFooter variant="landing" logoSrc={logo} />

        {showErrPopup && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#14142a] p-6 shadow-2xl">
              <h3 className="text-xl font-extrabold text-white mb-2">Account Not Registered</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                This Google account is not registered. Please select a plan and register.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closePopup}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Close
                </button>

                <button
                  onClick={() => {
                    closePopup();
                    navigate({ pathname: "/landing", hash: "#pricing" });
                  }}
                  className="px-4 py-2 rounded-lg bg-[#cb01bd] text-white font-bold hover:bg-[#e94ce0] transition"
                >
                  Select Plan
                </button>
              </div>
            </div>
          </div>
        )}
        {/* =====================================================
    SNAPSHOT → DEEP AUDIT POPUP
===================================================== */}

{showSnapshotPopup && (
  <div
    className="
      fixed
      inset-0
      z-[9999]
      flex
      items-center
      justify-center
      bg-black/70
      backdrop-blur-sm
      px-4
    "
    onClick={() =>
      setShowSnapshotPopup(false)
    }
  >
    <div
      className="
        w-full
        max-w-md
        rounded-2xl
        border
        border-[#cb01bd]/50
        bg-[#171525]
        p-6
        sm:p-8
        shadow-2xl
        text-center
      "
      onClick={(e) =>
        e.stopPropagation()
      }
    >
      <div className="text-3xl mb-3">
        ✨
      </div>

      <h3 className="text-2xl font-bold text-white mb-4">
        Try the Deep Audit instead
      </h3>

      <p className="text-slate-300 leading-relaxed mb-6">
        Use the Deep Dive instead.
        It&apos;s free for now and more detailed.
      </p>

      <div className="flex flex-col gap-3">

        <button
          type="button"
          onClick={() => {
            setShowSnapshotPopup(false);
            goToRegister("deep_audit");
          }}
          className="w-full py-3 rounded-lg bg-[#fbbf24] text-black font-bold hover:bg-yellow-400 transition"
        >
          Use Deep Audit →
        </button>

        <button
          type="button"
          onClick={() => {
            setShowSnapshotPopup(false);
            goToRegister("snapshot");
          }}
          className="w-full py-3 rounded-lg border border-[#cb01bd] text-[#cb01bd] font-bold hover:bg-[#cb01bd] hover:text-white transition"
        >
          Continue with Snapshot
        </button>

        <button
          type="button"
          onClick={() =>
            setShowSnapshotPopup(false)
          }
          className="mt-1 text-sm text-slate-400 hover:text-white transition"
        >
          Close
        </button>

      </div>
    </div>
  </div>
)}


</main>
</div>
);
}